import React, { useMemo, useCallback, useEffect } from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator'
import filterFactory from 'react-bootstrap-table2-filter'
import { Alert, Spinner } from 'react-bootstrap'
import { useLocalReceiptsState } from '../../stores/LocalReceiptsProvider'
import UniversalTableWrapperTest from '../Table/UniversalTableWrapperTest'
import TableProvider, { useTableState } from '../Table/TableProvider'
import useMountEffect from '../../utils/hooks/useMountEffect'
import { t } from 'i18next'
import { columns, receiptNameFilter } from './filterConfig.js'
import styled from 'styled-components'
import StructureIcon from '../Icons/StructureIcon'
import OverflowTooltip from '../../components/OverflowTooltip.jsx'

const StyledStructureButton = styled.button`
  border: 0;
  padding: 0;
  background: none;
  @media (max-width: 926px) {
    svg {
      width: 100% !important;
    }
  }
`
const StyledHeaderStructure = styled.div`
  display: flex;
  align-items: flex-end;

  // @media (max-width: 869px) {
  //   flex-direction: column !important;
  // }
`

const defaultSorted = [
  {
    dataField: 'displayName',
    order: 'desc',
  },
]

const LocalReceiptsTableInner = ({
  noActions,
  onRowClick,
  withCheckbox,
  tableId,
}) => {
  const {
    localReceipts,
    setPageOffset,
    setPageLimit,
    filter,
    setFilter,
    resetFilter,
    selectedRow,
    setShowAllLocalReceipts,
    setSorting,
    tableIsLoading,
    totalCount,
    selectAll,
    allCheckBoxesSelected,
    allSelectedLoading,
    setInitialTableId,
    initialTableId,
    setSelectedCheckBoxes,
    setOpenSupplierFilter,
    setOpenStorehouseFilter,
    setShowOverlayLeft,
    filterReceipt,
    setFilterReceipt,
  } = useLocalReceiptsState()

  const { tableRef, paginationOptions, currentPage, totalPages } =
    useTableState()

  useMountEffect(() => {
    if (tableId !== initialTableId) {
      setInitialTableId(tableId)
    }
    setSelectedCheckBoxes([])
  })

  useEffect(() => {
    //after Creating new Receipt auto output him
    if (!!filterReceipt) {
      setTimeout(() => {
        resetFilter()
        //set text in receiptName searchbar
        receiptNameFilter(filterReceipt)
        //set filter + trigger call
        setFilter((prev) => ({
          ...prev,
          'filter.receiptName': filterReceipt,
        }))
      }, 50)
      setFilterReceipt(null)
    }
  }, [filterReceipt, setFilterReceipt, resetFilter, setFilter])

  const handleTableChange = (type, newState) => {
    setPageLimit(newState.sizePerPage)
    if (type === 'filter') {
      setShowAllLocalReceipts(false)
      const { filters } = newState

      setFilter((prev) => ({
        ...prev,
        'filter.receiptName': filters['displayName']
          ? filters['displayName'].filterVal
          : '',
        'filter.supplierName': filters['supplier.displayName']
          ? filters['supplier.displayName'].filterVal
          : '',
        'filter.storehouseCoordinatesOrName': filters[
          'targetStorehouse.displayName'
        ]
          ? filters['targetStorehouse.displayName'].filterVal
          : '',
        'filter.targetStorehouse.displayName': filters[
          'targetStorehouse.displayName'
        ]
          ? filters['targetStorehouse.displayName'].filterVal
          : '',
      }))
    }

    if (type === 'sort') {
      const { sortOrder, sortField } = newState
      setSorting(`${sortOrder === 'asc' ? '-' : ''}${sortField}`)
    }

    setPageOffset(
      newState.page === 1
        ? 0
        : newState.sizePerPage * newState.page - newState.sizePerPage
    )
  }

  const handleStructureClick = useCallback(
    (dataField) => (e) => {
      e.stopPropagation()
      switch (dataField) {
        case 'targetStorehouse.displayName':
          setOpenStorehouseFilter((prev) => !prev)
          setOpenSupplierFilter(false)
          setShowOverlayLeft(true)
          break
        case 'supplier.displayName':
          setOpenStorehouseFilter(false)
          setOpenSupplierFilter((prev) => !prev)
          setShowOverlayLeft(true)
          break
        default:
      }
    },
    [setOpenStorehouseFilter, setOpenSupplierFilter, setShowOverlayLeft]
  )
  const preparedColumns = useMemo(() => {
    const tmpColumns = [...columns]
    tmpColumns.forEach((tmpColumn) => {
      tmpColumn.headerFormatter = (column, colIndex, components) => {
        switch (tmpColumn.dataField) {
          case 'checkBox':
            return (
              <span>
                {allSelectedLoading ? (
                  <Spinner animation='border' variant='primary' size='sm' />
                ) : (
                  <input
                    type='checkbox'
                    id='checkbox_header'
                    name='scales'
                    checked={allCheckBoxesSelected}
                    onChange={selectAll}
                  ></input>
                )}
              </span>
            )
          case 'targetStorehouse.displayName':
          case 'supplier.displayName':
            return (
              <>
                <OverflowTooltip>
                  {column.text}
                  {components.sortElement}
                  <StyledHeaderStructure>
                    {components.filterElement}
                    <div className='ps-2'>
                      <StyledStructureButton
                        onClick={handleStructureClick(tmpColumn.dataField)}
                      >
                        <StructureIcon />
                      </StyledStructureButton>
                    </div>
                  </StyledHeaderStructure>
                </OverflowTooltip>
              </>
            )
          default:
            return (
              <>
                <OverflowTooltip>
                  {column.text}
                  {components.sortElement}
                  <div className='d-flex align-items-end'>
                    {components.filterElement}
                  </div>
                </OverflowTooltip>
              </>
            )
        }
      }
    })
    tmpColumns.forEach((tmpColumn) => {
      tmpColumn.text = t(tmpColumn.text)
    })
    const ignoreColumns = () => {
      var result = []

      if (noActions) result.push('actions')
      if (!withCheckbox) result.push('checkBox')
      return result
    }
    return tmpColumns.filter(
      (item) => !ignoreColumns().includes(item.dataField)
    )
  }, [
    allSelectedLoading,
    allCheckBoxesSelected,
    selectAll,
    handleStructureClick,
    noActions,
    withCheckbox,
  ])

  const getChosenFilters = () => {
    var message = []
    //save filters in Array
    if (filter) {
      message = Object.keys(filter).filter((key) => filter[key] !== '')
    }
    return message
  }
  const noDataIndicationMessage = () => {
    const chosenFilters = getChosenFilters()
    const alertMessage = t('goodsReceiptsTable:alertMsg')
    //returns Alert if no results for used filters
    if (totalCount === 0 && chosenFilters.length > 0 && !tableIsLoading) {
      return (
        <div className='no-data-placeholder-alert'>
          <Alert variant='info'>{alertMessage}</Alert>
        </div>
      )
    }
    //returns empty Table
    else {
      return (
        <div className='no-data-placeholder'>
          {t('goodsReceiptsTable:pleaseSearchLocalReceipts')}
        </div>
      )
    }
  }
  return (
    <UniversalTableWrapperTest
      tableIsLoading={tableIsLoading}
      currentPage={currentPage}
      totalPages={totalPages}
    >
      <BootstrapTable
        ref={tableRef}
        remote
        loading={tableIsLoading}
        noDataIndication={() => noDataIndicationMessage()}
        onTableChange={handleTableChange}
        bootstrap4
        keyField='id'
        hover
        data={localReceipts}
        columns={preparedColumns}
        defaultSorted={defaultSorted}
        rowEvents={{ onClick: onRowClick }}
        rowClasses={(row, rowIndex) => {
          let classes = []
          if (row === selectedRow) {
            classes.push('active')
          }
          if (row.isDeleted) {
            classes.push('is-deleted')
          }
          return classes.join(' ')
        }}
        pagination={paginationFactory(paginationOptions)}
        filter={filterFactory()}
        bordered={false}
      />
    </UniversalTableWrapperTest>
  )
}

const LocalReceiptsTable = ({
  noActions,
  onRowClick,
  withCheckbox,
  tableId,
}) => {
  const {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  } = useLocalReceiptsState()

  const providerSettings = {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  }
  return (
    <TableProvider {...providerSettings}>
      <LocalReceiptsTableInner
        noActions={noActions}
        withCheckbox={withCheckbox}
        onRowClick={onRowClick}
        tableId={tableId}
      />
    </TableProvider>
  )
}

export default LocalReceiptsTable
